import { gql, type TypedDocumentNode } from '@apollo/client'
import { QueryRef, useReadQuery } from '@apollo/client/react'
import { ErrorAlert, PageHeader, PaginationControl, RouteLoadingIndicator } from '@repo/shared-client/components/blocks'
import { NativeSelectFormField } from '@repo/shared-client/components/fields'
import { Button, Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, stripSearchParams, useNavigate, useRouterState, useSearch } from '@tanstack/react-router'
import Footer from '@web/src/features/footer/footer'
import GalleryCard from '@web/src/features/gallery/gallery-card/gallery-card'
import {
  DEFAULT_GALLERY_MEDIA_TYPE,
  DEFAULT_GALLERY_NSFW_RATING,
  DEFAULT_GALLERY_SORT_BY,
  GALLERY_MEDIA_SORT_VALUES,
  GALLERY_MEDIA_TYPE_VALUES,
  GALLERY_NSFW_RATING_VALUES,
  type GalleryListFilterFormValues,
  type GalleryNsfwFilterRating,
  galleryListFilterFormValuesFromResolvedParams,
} from '@web/src/features/gallery/gallery-list-filter-types'
import GalleryListSideNav from '@web/src/features/gallery/gallery-list-side-nav/gallery-list-side-nav'
import GalleryListTopNav from '@web/src/features/gallery/gallery-list-top-nav/gallery-list-top-nav'
import { queryAndPreload } from '@web/src/lib/apollo/query-and-preload'
import { env } from '@web/src/lib/env.client'
import { MediaInfo } from '@web/src/lib/fragments'
import { useTranslation } from '@web/src/lib/intl/react'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { ImagesIcon, SlidersHorizontalIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GalleryQuery, GalleryQueryVariables, MediaSortEnum, MediaTypeEnum } from 'types/graphql'
import { z } from 'zod'

const GALLERY_QUERY: TypedDocumentNode<GalleryQuery, GalleryQueryVariables> = gql`
  query GalleryQuery($page: Int, $filter: MediaFilter, $sortBy: MediaSortEnum) {
    mediaListPage(page: $page, filter: $filter, sortBy: $sortBy) {
      medias {
        ...MediaInfo
      }
      count
      perPage
    }
  }
  ${MediaInfo}
`

const isMediaSortEnum = (value: string): value is MediaSortEnum => {
  return GALLERY_MEDIA_SORT_VALUES.includes(value as MediaSortEnum)
}

const isMediaTypeEnum = (value: string): value is MediaTypeEnum => {
  return GALLERY_MEDIA_TYPE_VALUES.includes(value as MediaTypeEnum)
}

const isGalleryNsfwRating = (value: string): value is GalleryNsfwFilterRating => {
  return GALLERY_NSFW_RATING_VALUES.includes(value as GalleryNsfwFilterRating)
}

type GalleryListLoaderDeps = {
  page: number
  sortBy: MediaSortEnum
  mediaType: 'ALL' | MediaTypeEnum
  nsfwRating: 'ALL' | GalleryNsfwFilterRating
}

type GalleryListSearchInput = {
  page?: number
  sortBy?: string
  mediaType?: string
  nsfwRating?: string
}

function resolveGalleryListSearchParams(search: GalleryListSearchInput): GalleryListLoaderDeps {
  return {
    page: search.page ?? 1,
    sortBy: search.sortBy && isMediaSortEnum(search.sortBy) ? search.sortBy : DEFAULT_GALLERY_SORT_BY,
    mediaType: search.mediaType && isMediaTypeEnum(search.mediaType) ? search.mediaType : DEFAULT_GALLERY_MEDIA_TYPE,
    nsfwRating:
      search.nsfwRating && isGalleryNsfwRating(search.nsfwRating) ? search.nsfwRating : DEFAULT_GALLERY_NSFW_RATING,
  }
}

function galleryListQueryVariablesFromDeps(deps: GalleryListLoaderDeps): GalleryQueryVariables {
  return {
    page: deps.page,
    sortBy: deps.sortBy,
    filter: {
      mediaType: deps.mediaType !== 'ALL' ? deps.mediaType : undefined,
      nsfwRating: deps.nsfwRating !== 'ALL' ? deps.nsfwRating : undefined,
      // Don't show current user private media in the gallery, it makes people concerned their
      // private photos are in the gallery.
      excludeCurrentUserPrivate: true,
    },
  }
}

export const Route = createFileRoute('/(auth)/(main-layout)/gallery')({
  component: GalleryPage,
  staticData: {
    hideFooter: true,
  },
  validateSearch: z.object({
    page: z.coerce.number().optional(),
    sortBy: z.string().optional(),
    mediaType: z.string().optional(),
    nsfwRating: z.string().optional(),
  }),
  search: {
    middlewares: [
      stripSearchParams({
        page: 1,
        sortBy: DEFAULT_GALLERY_SORT_BY,
        mediaType: DEFAULT_GALLERY_MEDIA_TYPE,
        nsfwRating: DEFAULT_GALLERY_NSFW_RATING,
      }),
    ],
  },
  head: () =>
    seo({
      title: `Gallery | ${env.VITE_APP_NAME}`,
      description: 'Gallery Media Page',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
  loaderDeps: ({ search }) => resolveGalleryListSearchParams(search),
  loader: async ({ context, deps: d }) => {
    const deps = d as GalleryListLoaderDeps
    const { queryRef } = await queryAndPreload(context, GALLERY_QUERY, {
      variables: galleryListQueryVariablesFromDeps(deps),
    })
    return { queryRef }
  },
})

function GalleryPage() {
  const { t } = useTranslation()
  const search = useSearch({ strict: false })
  const navigate = useNavigate()
  const { queryRef } = Route.useLoaderData()
  const { data, error } = useReadQuery(queryRef as QueryRef<GalleryQuery>)
  const searchRef = useRef(search)
  searchRef.current = search
  const isRoutePending = useRouterState({ select: (s) => s.status === 'pending' })
  const [filterNavigatePending, setFilterNavigatePending] = useState(false)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  useEffect(() => {
    if (!isRoutePending) {
      setFilterNavigatePending(false)
    }
  }, [isRoutePending])

  const isFilterLoading = filterNavigatePending && isRoutePending
  const resolvedSearch = useMemo(() => resolveGalleryListSearchParams(search), [search])

  const listFilterDefaults = useMemo(
    (): GalleryListFilterFormValues =>
      galleryListFilterFormValuesFromResolvedParams({
        mediaType: resolvedSearch.mediaType,
        nsfwRating: resolvedSearch.nsfwRating,
        sortBy: resolvedSearch.sortBy,
      }),
    [resolvedSearch.mediaType, resolvedSearch.nsfwRating, resolvedSearch.sortBy]
  )

  const listFilterResetValues = useMemo(
    (): GalleryListFilterFormValues =>
      galleryListFilterFormValuesFromResolvedParams({
        mediaType: DEFAULT_GALLERY_MEDIA_TYPE,
        nsfwRating: DEFAULT_GALLERY_NSFW_RATING,
        sortBy: DEFAULT_GALLERY_SORT_BY,
      }),
    []
  )

  const sortFormMethods = useForm<{ sortBy?: string }>({
    defaultValues: { sortBy: resolvedSearch.sortBy },
  })

  useEffect(() => {
    sortFormMethods.reset({ sortBy: resolvedSearch.sortBy })
  }, [resolvedSearch.sortBy, sortFormMethods])

  const applyListFilters = useCallback(
    (values: GalleryListFilterFormValues, options: { includeSort: boolean }) => {
      setFilterNavigatePending(true)
      navigate({
        search: {
          ...searchRef.current,
          page: 1,
          mediaType: values.mediaType === DEFAULT_GALLERY_MEDIA_TYPE ? undefined : values.mediaType,
          nsfwRating: values.nsfwRating === DEFAULT_GALLERY_NSFW_RATING ? undefined : values.nsfwRating,
          ...(options.includeSort ? { sortBy: values.sortBy || undefined } : {}),
        },
        replace: true,
      })
    },
    [navigate]
  )

  const handleSortChange = useCallback(() => {
    const sortBy = sortFormMethods.getValues('sortBy')
    if (sortBy && !isMediaSortEnum(sortBy)) return
    navigate({
      search: {
        ...searchRef.current,
        page: 1,
        sortBy: sortBy || undefined,
      },
      replace: true,
    })
  }, [navigate, sortFormMethods])

  const handleReset = useCallback(() => {
    navigate({
      to: '/gallery',
      replace: true,
    } as never)
  }, [navigate])

  const handlePageChange = useCallback(
    (nextPage: number): void => {
      navigate({
        search: {
          ...searchRef.current,
          page: nextPage,
        },
        replace: true,
      })
      window.scrollTo(0, 0)
    },
    [navigate]
  )

  const onSelectMedia = (mediaId: string) => {
    navigate({
      to: '/media/$mediaId',
      params: { mediaId },
      search: {
        disableCurrentUserPrivate: true,
        sortBy: resolvedSearch.sortBy,
        mediaType: resolvedSearch.mediaType,
        nsfwRating: resolvedSearch.nsfwRating,
      },
    } as never)
  }

  if (error) {
    return <ErrorAlert error={error} />
  }

  const mediaListPage = data?.mediaListPage ?? null
  const medias = mediaListPage?.medias ?? []

  return (
    <main className="flex min-h-0 w-full flex-col md:flex-row">
      <aside className="hidden w-fit shrink-0 overflow-visible md:sticky md:top-14 md:flex md:h-[calc(100dvh-3.5rem)] md:flex-col md:self-start">
        <GalleryListSideNav
          filterDefaults={listFilterDefaults}
          filterResetValues={listFilterResetValues}
          onFiltersApply={(values) => applyListFilters(values, { includeSort: false })}
          onReset={handleReset}
        />
      </aside>

      <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col">
        <RouteLoadingIndicator loading={isFilterLoading} size="xs" variant="progress" />
        <div className="mx-auto w-full max-w-none px-4 py-2 md:px-8 lg:px-16">
          <PageHeader
            breadcrumbItems={[{ label: t('Home'), to: '/' }, { label: t('Media Gallery') }]}
            className="items-stretch pb-0 pb-4 max-md:pb-0 md:mb-4"
            highlightTitle={t('Gallery')}
            preTitle={t('Media')}
            rightChild={
              <>
                <div className="flex items-start justify-end md:hidden">
                  <Button
                    aria-label={t('Toggle filters')}
                    onClick={() => setMobileFiltersOpen((open) => !open)}
                    size="icon"
                    type="button"
                    variant={mobileFiltersOpen ? 'outline' : 'default'}
                  >
                    <SlidersHorizontalIcon className="size-5" />
                  </Button>
                </div>
                <div className="hidden h-full flex-col items-end gap-3 md:flex">
                  <div className="mt-auto w-full">
                    <div className="flex flex-row items-center gap-2">
                      <span className="shrink-0 justify-self-center whitespace-nowrap font-medium text-muted-foreground text-sm">
                        {t('Sort By:')}
                      </span>
                      <Form formMethods={sortFormMethods} suppressHydrationWarning>
                        <NativeSelectFormField
                          fieldProps={{ className: 'min-w-0 flex-1' }}
                          name="sortBy"
                          onValueChange={handleSortChange}
                          selectProps={{ className: 'w-full min-w-[10rem]' }}
                        >
                          <option value="LATEST">{t('Latest')}</option>
                          <option value="COMMENTS">{t('Most Comments')}</option>
                          <option value="LIKES">{t('Most Likes')}</option>
                          <option value="VIEWS">{t('Most Views')}</option>
                        </NativeSelectFormField>
                      </Form>
                    </div>
                  </div>
                </div>
              </>
            }
            subtitle={t('Browse photos and videos shared by members.')}
          />

          <div className={mobileFiltersOpen ? 'mb-6 w-full md:hidden' : 'w-full md:hidden'}>
            <GalleryListTopNav
              filterDefaults={listFilterDefaults}
              filterResetValues={listFilterResetValues}
              onFiltersApply={(values) => applyListFilters(values, { includeSort: true })}
              onOpenChange={setMobileFiltersOpen}
              onReset={handleReset}
              open={mobileFiltersOpen}
            />
          </div>

          {medias.length > 0 ? (
            <div className="flex w-full flex-col items-stretch gap-0">
              <div className="grid w-full grid-cols-2 flex-wrap justify-center gap-3 sm:flex">
                {medias.map((media) => (
                  <GalleryCard key={media.id} media={media} onSelectMedia={onSelectMedia} />
                ))}
              </div>

              <PaginationControl
                count={mediaListPage?.count ? mediaListPage.count : 1}
                onPageChange={(e) => handlePageChange(e.page)}
                page={resolvedSearch.page}
                pageSize={mediaListPage?.perPage ? mediaListPage.perPage : 1}
                siblingCount={1}
              />
            </div>
          ) : (
            <Empty className="w-full pb-20">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <ImagesIcon />
                </EmptyMedia>
                <EmptyTitle>{t('No results')}</EmptyTitle>
                <EmptyDescription>{t('Try adjusting your search or filters.')}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </div>

        <Footer />
      </div>
    </main>
  )
}
